import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSharedStore } from '../../hooks/useSharedStore';
import { shifts, addShift, updateShift, deleteShift, updateOrganization, users, updateUser, effectiveBranchesOf } from '../../data/store';
import { Plus, Clock, Store, Trash2, Moon, AlertCircle, Coffee, Users, CheckSquare, Square, Building2, Tag, Timer, Utensils, Edit2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomSelect } from '../../components/CustomSelect';
import { CircularTimePicker as TimeInput } from '../../components/CircularTimePicker';

export const MasterShiftsTab = ({ org, showNotification }) => {
  useSharedStore();
  const accent = org?.theme?.primary || 'var(--theme-primary)';
  
  const [showShiftModal, setShowShiftModal] = useState(false);
  
  const defaultDraft = { 
    name: '', 
    code: '',
    startTime: '08:00', 
    endTime: '16:00',
    graceIn: 15,
    graceOut: 15,
    branchId: '',
    department: '',
    status: 'Active',
    breaks: [], 
    assignedStaffIds: [] 
  };
  
  const [draftShift, setDraftShift] = useState(defaultDraft);
  const [shiftError, setShiftError] = useState('');

  const myShifts = shifts.filter(s => s.orgId === org?.id);
  const myStaff = users.filter(u => u.orgId === org?.id && u.role !== 'super-admin');
  const myBranches = effectiveBranchesOf(org?.id);



  const handleAddBreak = (type) => {
    const isLunch = type === 'lunch';
    setDraftShift(d => ({
      ...d,
      breaks: [...(d.breaks || []), { 
        id: Date.now().toString(), 
        type: type,
        name: isLunch ? 'Lunch Break' : 'Tea Break', 
        startTime: isLunch ? '13:00' : '16:00', 
        endTime: isLunch ? '14:00' : '16:15' 
      }]
    }));
  };

  const updateBreak = (id, field, value) => {
    setDraftShift(d => ({
      ...d,
      breaks: d.breaks.map(b => b.id === id ? { ...b, [field]: value } : b)
    }));
  };

  const removeBreak = (id) => {
    setDraftShift(d => ({ ...d, breaks: d.breaks.filter(b => b.id !== id) }));
  };

  const toggleStaffAssignment = (userId) => {
    setDraftShift(d => {
      const current = d.assignedStaffIds || [];
      if (current.includes(userId)) {
        return { ...d, assignedStaffIds: current.filter(id => id !== userId) };
      } else {
        return { ...d, assignedStaffIds: [...current, userId] };
      }
    });
  };

  const openEditModal = (shift) => {
    const currentlyAssigned = myStaff.filter(u => u.defaultShiftId === shift.id).map(u => u.id);
    setDraftShift({ ...defaultDraft, ...shift, assignedStaffIds: currentlyAssigned });
    setShiftError('');
    setShowShiftModal(true);
  };

  const handleSaveShift = () => {
    if (!draftShift.name.trim()) {
      setShiftError('Shift name is required.');
      return;
    }
    
    if (!draftShift.code.trim()) {
      setShiftError('Shift Code is required (e.g. MORN-1).');
      return;
    }
    
    const isOvernight = draftShift.endTime <= draftShift.startTime;
    

    let finalShiftId = draftShift.id;
    const shiftData = { ...draftShift, isOvernight, orgId: org.id };
    delete shiftData.assignedStaffIds; 

    if (finalShiftId) {
      updateShift(finalShiftId, shiftData);
    } else {
      finalShiftId = Date.now().toString(); 
      addShift({ ...shiftData, id: finalShiftId });
    }

    // Process staff assignments
    myStaff.forEach(staff => {
      const isSelected = draftShift.assignedStaffIds.includes(staff.id);
      if (isSelected && staff.defaultShiftId !== finalShiftId) {
        updateUser(staff.id, { defaultShiftId: finalShiftId });
      } else if (!isSelected && staff.defaultShiftId === finalShiftId) {
        updateUser(staff.id, { defaultShiftId: null });
      }
    });
    
    setShowShiftModal(false);
    setDraftShift(defaultDraft);
    setShiftError('');
  };

  const formatTimeStr = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    let hr = parseInt(h, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${m} ${ampm}`;
  };

  return (
    <div className="flex flex-col h-full gap-4">

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
        
        {/* Shift Rules Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-b border-[var(--theme-tertiary)] bg-slate-50 shrink-0">
          <div className="flex items-center gap-2 shrink-0">
            <Clock size={16} style={{ color: accent }} />
            <h3 className="text-[13px] font-extrabold text-[#0d0d0d] uppercase tracking-widest">Master Roster</h3>
          </div>

          <button 
            onClick={() => { setDraftShift(defaultDraft); setShiftError(''); setShowShiftModal(true); }} 
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-widest text-white shadow-sm transition-opacity hover:opacity-90 active:scale-95 shrink-0"
            style={{ background: accent }}
          >
            <Plus size={14} /> Add Shift Rule
          </button>
        </div>
        
        {/* Main Content Area */}
        <div className="p-5 overflow-auto custom-scrollbar bg-slate-50/50 flex-1">

        
        {myShifts.length === 0 ? (
          <div className="py-10 text-center flex flex-col items-center justify-center">
            <Clock size={28} className="text-slate-300 mb-2" />
            <p className="text-sm font-medium text-slate-400">No shift rules created yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myShifts.map(s => {
              const assignedCount = myStaff.filter(u => u.defaultShiftId === s.id).length;
              return (
                <div 
                  key={s.id} 
                  onClick={() => openEditModal(s)}
                  className={`border ${s.status === 'Inactive' ? 'border-slate-200 bg-slate-50/50 opacity-60' : 'border-[var(--theme-tertiary)] bg-slate-50'} rounded-lg p-3 flex flex-col relative group cursor-pointer hover:border-[var(--theme-primary)] hover:shadow-md transition-all`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-[13px] font-extrabold text-[#0d0d0d]">{s.name}</h4>
                    <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest">
                      {s.code}
                    </span>
                    {s.isOvernight && (
                      <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                        <Moon size={10} /> Overnight
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs font-semibold text-slate-600 mb-2">
                    <div>{formatTimeStr(s.startTime)}</div>
                    <div className="w-3 h-px bg-slate-300"></div>
                    <div>{formatTimeStr(s.endTime)}</div>
                  </div>

                  {s.breaks && s.breaks.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {s.breaks.map(b => (
                        <span key={b.id} className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${b.type === 'lunch' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
                          {b.type === 'lunch' ? <Utensils size={9} className="text-amber-500" /> : <Coffee size={9} className="text-sky-500" />} {b.name}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-auto pt-3 border-t border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      <Users size={12} /> {assignedCount} Staff Assigned
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={(e) => { e.stopPropagation(); openEditModal(s); }}
                        className="p-1.5 rounded-md bg-sky-50 text-sky-600 hover:bg-sky-600 hover:text-white transition-colors border border-sky-100"
                        title="Edit Shift"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); deleteShift(s.id); }}
                        className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors border border-red-100"
                        title="Delete Shift"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </section>

      {/* Add/Edit Shift Modal */}
      <AnimatePresence>
        {showShiftModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }} 
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-[var(--theme-tertiary)] bg-white shrink-0 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-[#0d0d0d] uppercase tracking-widest flex items-center gap-2">
                  <Store size={16} className="text-slate-400" />
                  {draftShift.id ? 'Edit Shift Rule' : 'Create New Dynamic Shift Rule'}
                </h3>
                <button 
                  onClick={() => setShowShiftModal(false)}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
              
              <div className="p-0 overflow-auto custom-scrollbar flex flex-col md:flex-row">
                
                {/* Left Col: Master Shift Settings */}
                <div className="flex-1 flex flex-col p-6 border-r border-slate-100">
                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Tag size={14} /> Core Configuration
                  </h4>

                  {shiftError && (
                    <div className="p-2.5 bg-red-50 border border-red-100 rounded-md flex gap-2 text-red-600 mb-4 shrink-0">
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                      <p className="text-[11px] font-bold">{shiftError}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-6 gap-4 mb-4">
                    <div className="col-span-3">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Shift Name *</label>
                      <input 
                        type="text" 
                        value={draftShift.name} 
                        onChange={(e) => setDraftShift(d => ({...d, name: e.target.value}))} 
                        placeholder="e.g. Morning Shift" 
                        className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md px-3 py-2 text-[13px] font-bold text-[#0d0d0d] outline-none transition-colors" 
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Shift Code *</label>
                      <input 
                        type="text" 
                        value={draftShift.code} 
                        onChange={(e) => setDraftShift(d => ({...d, code: e.target.value.toUpperCase()}))} 
                        placeholder="e.g. MR" 
                        className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md px-3 py-2 text-[13px] font-bold text-[#0d0d0d] outline-none transition-colors uppercase" 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-6 gap-4 mb-4">
                    <div className="col-span-3">
                      <TimeInput label="Start Time" value={draftShift.startTime} onChange={(val) => setDraftShift(d => ({...d, startTime: val}))} />
                    </div>
                    <div className="col-span-3">
                      <TimeInput label="End Time" value={draftShift.endTime} onChange={(val) => setDraftShift(d => ({...d, endTime: val}))} />
                    </div>
                  </div>

                  <div className="grid grid-cols-6 gap-4 mb-4">
                    <div className="col-span-3">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Grace In (Mins)</label>
                      <input 
                        type="number" 
                        value={draftShift.graceIn} 
                        onChange={(e) => setDraftShift(d => ({...d, graceIn: parseInt(e.target.value) || 0}))} 
                        className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md px-3 py-2 text-[13px] font-bold text-[#0d0d0d] outline-none transition-colors" 
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Grace Out (Mins)</label>
                      <input 
                        type="number" 
                        value={draftShift.graceOut} 
                        onChange={(e) => setDraftShift(d => ({...d, graceOut: parseInt(e.target.value) || 0}))} 
                        className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md px-3 py-2 text-[13px] font-bold text-[#0d0d0d] outline-none transition-colors" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-6 gap-4 mb-5">
                    <div className="col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Service Location</label>
                      <CustomSelect 
                        value={draftShift.branchId} 
                        onChange={(val) => setDraftShift(d => ({...d, branchId: val}))} 
                        options={[
                          { value: '', label: 'All Locations' },
                          ...myBranches.map(b => ({ value: b.id, label: b.name }))
                        ]}
                        className="w-full bg-[var(--theme-secondary)] border-[var(--theme-tertiary)] focus:bg-white text-[13px] font-bold text-[#0d0d0d]"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Target Role</label>
                      <input 
                        type="text" 
                        value={draftShift.department} 
                        onChange={(e) => setDraftShift(d => ({...d, department: e.target.value}))} 
                        placeholder="e.g. Stylist" 
                        className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md px-3 py-2 text-[13px] font-bold text-[#0d0d0d] outline-none transition-colors" 
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Status</label>
                      <CustomSelect
                        value={draftShift.status}
                        onChange={(val) => setDraftShift(d => ({ ...d, status: val }))}
                        options={[
                          { value: 'Active', label: 'Active' },
                          { value: 'Inactive', label: 'Inactive' }
                        ]}
                        className="w-full bg-[var(--theme-secondary)] border-[var(--theme-tertiary)] focus:bg-white text-[13px] font-bold text-[#0d0d0d]"
                      />
                    </div>
                  </div>

                  {/* Breaks Section */}
                  <div className="border border-[var(--theme-tertiary)] rounded-md overflow-hidden bg-slate-50 mt-4 shrink-0 shadow-sm">
                    <div className="px-3 py-2 border-b border-[var(--theme-tertiary)] flex justify-between items-center bg-slate-100">
                      <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                        <Clock size={12} /> Scheduled Breaks
                      </span>
                      <div className="flex items-center gap-2">
                        <button 
                          type="button" 
                          onClick={() => handleAddBreak('tea')} 
                          disabled={draftShift.breaks?.filter(b => b.type === 'tea').length >= 2}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:grayscale transition-colors text-[10px] font-bold uppercase tracking-widest border border-blue-100"
                        >
                          <Coffee size={10} strokeWidth={2.5} /> + Tea Break
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleAddBreak('lunch')} 
                          disabled={draftShift.breaks?.filter(b => b.type === 'lunch').length >= 1}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50 disabled:grayscale transition-colors text-[10px] font-bold uppercase tracking-widest border border-red-100"
                        >
                          <Utensils size={10} strokeWidth={2.5} /> + Lunch Break
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-3 flex flex-col gap-3 min-h-[60px]">
                      {(!draftShift.breaks || draftShift.breaks.length === 0) ? (
                        <p className="text-[11px] font-medium text-slate-400 text-center py-2">No breaks scheduled for this shift.</p>
                      ) : (
                        draftShift.breaks.map((b) => (
                          <div key={b.id} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-slate-200 relative overflow-hidden shadow-sm group hover:border-[var(--theme-primary)] transition-colors">
                            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: b.type === 'lunch' ? '#f59e0b' : '#0ea5e9' }}></div>
                            
                            <div className="flex-1 pl-2">
                              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                {b.type === 'lunch' ? <Utensils size={10} className="text-amber-500" /> : <Coffee size={10} className="text-sky-500" />} {b.type === 'lunch' ? 'Lunch Break' : 'Tea Break'}
                              </label>
                              <input 
                                type="text" 
                                value={b.name} 
                                onChange={(e) => updateBreak(b.id, 'name', e.target.value)} 
                                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] rounded-md px-3 py-1.5 text-xs font-bold text-[#0d0d0d] outline-none focus:bg-white transition-colors" 
                              />
                            </div>
                            
                            <div className="w-24">
                              <TimeInput label="Start" value={b.startTime} onChange={(val) => updateBreak(b.id, 'startTime', val)} />
                            </div>
                            
                            <div className="w-24">
                              <TimeInput label="End" value={b.endTime} onChange={(val) => updateBreak(b.id, 'endTime', val)} />
                            </div>
                            
                            <div className="pt-[18px]">
                              <button onClick={() => removeBreak(b.id)} className="p-1.5 mt-0.5 text-slate-400 hover:text-red-600 rounded-md bg-slate-50 border border-transparent hover:border-red-100 hover:bg-red-50 transition-colors shrink-0">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Col: Staff Assignment */}
                <div className="w-72 bg-slate-50 flex flex-col shrink-0">
                  <div className="p-5 border-b border-slate-200">
                    <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Users size={14} /> Service Providers
                    </h4>
                    <p className="text-[10px] text-slate-400 leading-relaxed mb-3">
                      Select providers to permanently assign to this shift. Their calendar will be <strong>dynamically auto-scheduled</strong> for customer bookings every week.
                    </p>
                    {/* Dynamic Filters Indicator */}
                    {(draftShift.branchId || draftShift.department) && (
                      <div className="bg-sky-50 border border-sky-100 rounded-md p-2 flex flex-col gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-sky-600">Dynamic Filters Applied:</span>
                        {draftShift.branchId && <span className="text-[10px] font-semibold text-slate-600 flex items-center gap-1"><Store size={8} /> Location Filtered</span>}
                        {draftShift.department && <span className="text-[10px] font-semibold text-slate-600 flex items-center gap-1"><Tag size={8} /> Role: {draftShift.department}</span>}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col overflow-auto custom-scrollbar flex-1 p-3 gap-1.5">
                    {(() => {
                      const filteredStaff = myStaff.filter(staff => {
                        if (draftShift.branchId && staff.branchId !== draftShift.branchId) return false;
                        if (draftShift.department && !staff.role.toLowerCase().includes(draftShift.department.toLowerCase())) return false;
                        return true;
                      });

                      if (filteredStaff.length === 0) {
                        return <p className="text-xs text-slate-400 italic text-center mt-4">No matching staff found.</p>;
                      }

                      return filteredStaff.map(staff => {
                        const isSelected = draftShift.assignedStaffIds.includes(staff.id);
                        return (
                          <div 
                            key={staff.id} 
                            onClick={() => toggleStaffAssignment(staff.id)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer border transition-colors ${isSelected ? 'bg-sky-50 border-sky-200 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                          >
                            <div className={isSelected ? 'text-[var(--theme-primary)]' : 'text-slate-300'}>
                              {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                            </div>
                            <div className="min-w-0">
                              <span className={`block text-[13px] font-bold truncate ${isSelected ? 'text-sky-900' : 'text-slate-700'}`}>{staff.name}</span>
                              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{staff.role.replace('-',' ')}</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

              </div>
              
              <div className="p-4 border-t border-[var(--theme-tertiary)] bg-white flex justify-end gap-3 shrink-0">
                <button 
                  onClick={() => setShowShiftModal(false)} 
                  className="px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveShift}
                  disabled={!draftShift.name || !draftShift.code}
                  className="px-5 py-2.5 bg-[var(--theme-primary)] text-white text-[11px] font-extrabold uppercase tracking-widest rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
                >
                  {draftShift.id ? 'Save Changes' : 'Save Shift Configuration'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
